/**
 * CO2e for one unit.
 *
 * THE RULE THIS COMPONENT EXISTS TO HOLD: measured and counterfactual are never
 * merged, and the result never renders without both visible above it on the same
 * screen. Not in a tooltip, not behind an expander — a collapsed counterfactual
 * is a flattened counterfactual. `saving_pct` in particular is never a standalone
 * hero number: that is the figure that gets screenshotted into a deck and loses
 * its context, which is exactly how a "85% CO2e reduction" claim survived three
 * years with no derivation behind it.
 *
 * Publication status is an ANNOTATION, never a filter. A figure below the public
 * threshold is still true and is still shown here in full — hiding it from the
 * operators who produced it would be wrong, and would make this screen and the
 * public page disagree in a way neither could explain.
 *
 * There is no "generate report" control, deliberately. Storing a report needs
 * the write token, which this client does not hold. See api/client.ts.
 */
import type {
  Co2eActual,
  Co2eCounterfactual,
  Co2eMeasured,
  Co2eReportListItem,
  Co2eResponse,
  Co2eResult,
} from '../api/types';
import { DASH, kwh, num, pct, stamp } from './format';

const METHODOLOGY_URL =
  'https://github.com/Equoia/equoia-fleet/blob/main/docs/CO2E_METHODOLOGY.md';

function kg(v: number | null | undefined): string {
  return v == null ? DASH : `${num(v, 1)} kg`;
}
function litres(v: number | null | undefined): string {
  return v == null ? DASH : `${num(v, 1)} L`;
}

/** Telemetry. True regardless of whether a baseline could be computed, so this
 *  renders even on a refusal — blanking it would teach an operator that a
 *  missing constant means missing data. */
function Measured({ m }: { m: Co2eMeasured }) {
  return (
    <div className="co2e-block">
      <div className="co2e-block__head">
        Measured <span className="co2e-block__tag">from telemetry</span>
      </div>
      <dl className="kv">
        <dt>Generator runtime</dt>
        <dd>{num(m.genset_runtime_h, 2)} h</dd>
        <dt>Generator energy</dt>
        <dd>{kwh(m.genset_energy_kwh)} kWh</dd>
        <dt>Mean generator load</dt>
        <dd>{m.genset_mean_load == null ? DASH : pct(m.genset_mean_load * 100)}</dd>
        <dt>Mains charging</dt>
        <dd>{kwh(m.mains_charge_kwh)} kWh</dd>
        <dt>Solar</dt>
        <dd>{kwh(m.solar_kwh)} kWh</dd>
        <dt>Load served</dt>
        <dd>{kwh(m.load_served_kwh)} kWh</dd>
        <dt>Coverage</dt>
        <dd>{pct(m.coverage * 100)}</dd>
      </dl>
    </div>
  );
}

/** The generator that did not run. Visually set apart, and the assumption is
 *  rendered BEFORE the numbers it produced so it is read first. */
function Counterfactual({ c }: { c: Co2eCounterfactual }) {
  return (
    <div className="co2e-block co2e-block--modelled">
      <div className="co2e-block__head">
        Counterfactual <span className="co2e-block__tag">modelled, not measured</span>
      </div>
      <p className="co2e-assumption">{c.assumption}</p>
      <dl className="kv">
        <dt>Baseline runtime</dt>
        <dd>{c.baseline_runtime_h == null ? DASH : `${num(c.baseline_runtime_h, 2)} h`}</dd>
        <dt>Baseline load</dt>
        <dd>{c.baseline_load_frac == null ? DASH : pct(c.baseline_load_frac * 100)}</dd>
        <dt>Baseline fuel</dt>
        <dd>{litres(c.baseline_fuel_l)}</dd>
        <dt>Baseline CO₂e</dt>
        <dd>{kg(c.baseline_co2e_kg)}</dd>
      </dl>
    </div>
  );
}

function Actual({ a }: { a: Co2eActual }) {
  return (
    <div className="co2e-block">
      <div className="co2e-block__head">Actual</div>
      <dl className="kv">
        <dt>Fuel burned</dt>
        <dd>{litres(a.actual_fuel_l)}</dd>
        <dt>Fuel CO₂e</dt>
        <dd>{kg(a.actual_fuel_co2e_kg)}</dd>
        <dt>Grid CO₂e</dt>
        <dd>{kg(a.actual_grid_co2e_kg)}</dd>
        <dt>Total</dt>
        <dd>{kg(a.actual_co2e_kg)}</dd>
      </dl>
    </div>
  );
}

function Result({ r, version }: { r: Co2eResult; version: string }) {
  return (
    <div className="co2e-result">
      <div className="co2e-result__figure">{kg(r.saving_kg)}</div>
      <div className="co2e-result__note">
        estimated CO₂e avoided
        {r.saving_pct != null && <> · {pct(r.saving_pct * 100, 1)} of baseline</>}
      </div>
      <div className="co2e-result__meta">
        Estimate — fuel is modelled from runtime and load, never metered. Method{' '}
        {version}. <a href={METHODOLOGY_URL}>How this is calculated</a>
      </div>
    </div>
  );
}

/** The full breakdown, shared by preview and stored report so the two cannot
 *  drift into showing different things. */
function Breakdown({
  measured,
  counterfactual,
  actual,
  result,
  version,
}: {
  measured: Co2eMeasured;
  counterfactual: Co2eCounterfactual;
  actual: Co2eActual;
  result: Co2eResult;
  version: string;
}) {
  return (
    <>
      <div className="co2e-grid">
        <Measured m={measured} />
        <Counterfactual c={counterfactual} />
        <Actual a={actual} />
      </div>
      <Result r={result} version={version} />
    </>
  );
}

function ReportStatus({ r }: { r: Co2eReportListItem }) {
  if (r.withdrawn_at) {
    return (
      <span className="co2e-status co2e-status--history">
        withdrawn {stamp(r.withdrawn_at)}
        {r.withdrawn_reason ? ` — ${r.withdrawn_reason}` : ''}
      </span>
    );
  }
  if (r.superseded_by != null) {
    return (
      <span className="co2e-status co2e-status--history">
        superseded by report #{r.superseded_by}
      </span>
    );
  }
  return <span className="co2e-status co2e-status--live">counts toward the public total</span>;
}

export function Co2ePanel({
  preview,
  reports,
  error,
}: {
  preview: Co2eResponse | null;
  reports: Co2eReportListItem[] | null;
  error?: string | null;
}) {
  if (error) return <div className="note bad">{error}</div>;
  if (!preview) return <div className="hint">Loading…</div>;

  const live = (reports ?? []).filter((r) => r.is_live);
  const refused = preview.incomplete_reason != null;

  return (
    <>
      {refused && (
        <div className="unavailable-panel">
          <strong>No CO₂e figure for this unit</strong>
          {preview.incomplete_reason}
          <div style={{ marginTop: 6 }}>
            A substituted default would be a number nobody chose. The measured
            facts below are unaffected.
          </div>
        </div>
      )}

      {/* Renders on a refusal too: these are telemetry, and they are true. */}
      <div className="co2e-grid">
        <Measured m={preview.measured} />
        {!refused && <Counterfactual c={preview.counterfactual} />}
        {!refused && <Actual a={preview.actual} />}
      </div>
      {!refused && <Result r={preview.result} version={preview.methodology_version} />}

      {!refused && live.length === 0 && (
        <div className="hint" style={{ marginTop: 10 }}>
          This is a live preview and is not stored, so it does not count toward
          the public total. Storing a report needs the write token and is done
          deliberately, by script.
        </div>
      )}

      {live.length > 0 && (
        <div className="co2e-stored">
          <div className="co2e-block__head">Stored reports</div>
          {live.map((r) => {
            /* Preview and stored figure can differ, and when they do that IS the
               information: a constant changed since the report was stored. Show
               both and say so, rather than silently preferring either. */
            const drift =
              !refused &&
              r.result.saving_kg != null &&
              preview.result.saving_kg != null &&
              Math.abs(r.result.saving_kg - preview.result.saving_kg) > 0.05;
            return (
              <div key={r.id} className="co2e-report">
                <div className="co2e-report__head">
                  <strong>#{r.id}</strong> {stamp(r.from)} → {stamp(r.to)}
                  <span className="sub">
                    {' '}
                    by {r.generated_by ?? r.generated_by_token} ·{' '}
                    {stamp(r.generated_at)}
                  </span>
                  <ReportStatus r={r} />
                </div>
                {drift && (
                  <div className="note warn">
                    Recomputing this window today gives{' '}
                    {kg(preview.result.saving_kg)}, not {kg(r.result.saving_kg)}.
                    The stored report is what was published; the difference means
                    a constant changed since. Neither is wrong — the stored one is
                    the record, the preview is today's method.
                  </div>
                )}
                <Breakdown
                  measured={r.measured}
                  counterfactual={r.counterfactual}
                  actual={r.actual}
                  result={r.result}
                  version={r.methodology_version}
                />
                {r.note && <p className="co2e-assumption">{r.note}</p>}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
